// src/components/solution_sections/GettingStartedInquirySection.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';

import { Card } from '@dopomogai/ui';
import { Button } from '@dopomogai/ui';

// Define the data shapes for this component
interface PrepPoint {
    question: string;
    examples?: string;
}

// --- THIS IS THE FIX ---
// The types for `variant` and `size` have been corrected to match what the
// Button component from `@dopomogai/ui` actually accepts.
// - `variant` no longer includes "link".
// - `size` no longer includes "icon".
interface CtaData {
    text: string;
    actionType: string;
    actionPayload: any;
    variant?: "primary" | "secondary" | "outline" | "ghost";
    size?: "sm" | "md" | "lg";
}

interface GettingStartedData {
    sectionTitle?: string;
    icon?: string;
    introduction: string;
    primaryCta: CtaData;
    preparationPoints: {
        header: string;
        points: PrepPoint[];
    };
}

interface GettingStartedInquirySectionProps {
  data: GettingStartedData;
  id: string;
  headerBgColor?: string;
  onAction: (actionType: string, payload?: any) => void;
}

const listContainerVariants = {
  visible: { transition: { staggerChildren: 0.15 } },
  hidden: {},
};

const listItemVariants = {
  visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  hidden: { opacity: 0, x: -20 },
};

export const GettingStartedInquirySection = React.forwardRef<HTMLDivElement, GettingStartedInquirySectionProps>(
  ({ data, id, headerBgColor, onAction }, ref) => {
    const { t } = useTranslation('solution_showcase');

    if (!data) return null;

    return (
      <Card
        ref={ref} 
        title={data.sectionTitle || t('gettingStarted.defaultTitle')}
        icon={data.icon || 'rocket'}
        id={id}
        headerBgColor={headerBgColor}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          whileHover={{ y: -4, boxShadow: '4px 4px 0px 0px #0d9488' }}
          className="bg-slate-100 p-6 md:p-10 rounded-xl border-2 border-black text-center"
        >
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
              {data.introduction}
          </p>

          {data.primaryCta && (
            <Button
              text={data.primaryCta.text}
              variant={data.primaryCta.variant || 'primary'}
              size={data.primaryCta.size || 'lg'}
              onClick={() => onAction(data.primaryCta.actionType, data.primaryCta.actionPayload)}
              className="mx-auto"
            />
          )}

          {data.preparationPoints && data.preparationPoints.points.length > 0 && (
            <div className="mt-8 pt-6 border-t border-slate-200 max-w-lg mx-auto text-left">
              <h4 className="text-sm font-semibold text-gray-600 mb-3 text-center">
                {data.preparationPoints.header}
              </h4>
              <motion.ul
                className="space-y-3"
                variants={listContainerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.5 }}
              >
                {data.preparationPoints.points.map((point, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start gap-3 text-sm"
                    variants={listItemVariants}
                  >
                    <CheckCircle size={18} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-gray-800">
                          {point.question}
                      </span>
                      {point.examples && (
                        <span className="text-gray-500 ml-1">
                            {point.examples}
                        </span>
                      )}
                    </div>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          )}
        </motion.div>
      </Card>
    );
  }
);